/**
 * ga.js — puente con Google Tag Manager / GA4.
 *
 * GTM se carga desde `index.html` (con Consent Mode v2 por defecto en
 * `denied`) en la primera ruta no excluida que pise la sesión, sea en la carga
 * dura o navegando por la SPA (`components/CookieConsent.jsx`); aquí solo hay
 * dos cosas:
 *
 * 1. `pushEvent()` empuja un evento al `dataLayer`. Qué hace GA4 con él lo
 *    decide el contenedor de GTM, no este código. Sin GTM (dev, tests, rutas
 *    excluidas) es un no-op.
 *
 * 2. `getGaIds()` lee el `client_id` y el `session_id` de GA4 de sus cookies
 *    para mandarlos al backend en el registro y en el checkout. Con ellos el
 *    webhook de Stripe puede enviar a GA4 el trial y el pago **en nombre de
 *    este visitante** (Measurement Protocol, `QR/billing/ga4.py`), que es lo
 *    que permite atribuir una venta al canal del que vino. Si el visitante
 *    rechazó la analítica no hay cookie `_ga`, devolvemos nulls y el backend no
 *    envía nada: es a propósito.
 *
 * Como el tracking de CTAs, nada de esto puede romper un registro ni un pago:
 * todo va en `try/catch`.
 */

/** Empuja `{ event: name, ...params }` al dataLayer de GTM, si existe. */
export function pushEvent(name, params = {}) {
  try {
    if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) return;
    window.dataLayer.push({ event: name, ...params });
  } catch {
    // el tracking nunca rompe la acción que lo dispara
  }
}

function readCookie(name) {
  const prefix = `${name}=`;
  const found = document.cookie.split(';').map((c) => c.trim()).find((c) => c.startsWith(prefix));
  return found ? decodeURIComponent(found.slice(prefix.length)) : null;
}

/**
 * `_ga` = `GA1.1.<random>.<timestamp>` → client_id `<random>.<timestamp>`.
 * El segundo número es la profundidad de dominio y puede variar (GA1.2…).
 */
export function parseClientId(value) {
  const m = /^GA\d\.\d+\.(\d{1,20}\.\d{1,20})$/.exec(value || '');
  return m ? m[1] : null;
}

/**
 * `_ga_<ID>` tiene dos formatos vivos:
 *   antiguo: `GS1.1.<session_id>.<n>.<…>`
 *   nuevo:   `GS2.1.s<session_id>$o<n>$g…`
 */
export function parseSessionId(value) {
  const v = value || '';
  const nuevo = /^GS2\.\d+\.s(\d{1,20})(?:\$|$)/.exec(v);
  if (nuevo) return nuevo[1];
  const antiguo = /^GS1\.\d+\.(\d{1,20})(?:\.|$)/.exec(v);
  return antiguo ? antiguo[1] : null;
}

/**
 * ¿Aceptó el visitante las cookies de PUBLICIDAD en CookieYes? Es distinto de
 * la analítica: `_ga` existe con aceptar solo analítica, pero a Google Ads solo
 * se le puede ceder el dato con consentimiento de publicidad. El backend lo
 * manda a GA4 como `consent.ad_user_data`.
 */
function adConsent() {
  try {
    return window.getCkyConsent?.()?.categories?.advertisement === true;
  } catch {
    return false;
  }
}

const SIN_IDS = { clientId: null, sessionId: null, adConsent: false };

/**
 * `{ clientId, sessionId, adConsent }` de GA4, o nulls si no hay cookies (sin
 * GTM, sin consentimiento o sin `VITE_GA4_MEASUREMENT_ID` para la de sesión).
 */
export function getGaIds() {
  try {
    if (typeof document === 'undefined') return SIN_IDS;
    const clientId = parseClientId(readCookie('_ga'));
    if (!clientId) return SIN_IDS;
    const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID || '';
    const sessionCookie = measurementId ? readCookie(`_ga_${measurementId.replace(/^G-/, '')}`) : null;
    return { clientId, sessionId: parseSessionId(sessionCookie), adConsent: adConsent() };
  } catch {
    return SIN_IDS;
  }
}

/** Los campos `ga_*` para el cuerpo de una petición al backend, o `{}`. */
export function gaIdsPayload() {
  const { clientId, sessionId, adConsent: ads } = getGaIds();
  if (!clientId) return {};
  return {
    ga_client_id: clientId,
    ...(sessionId ? { ga_session_id: sessionId } : {}),
    ga_ad_consent: ads,
  };
}
